import Websocket from "@skydio/comms/src/ws/reconnecting_websocket";
import { web_socket_msg_t } from "@skydio/lcm/types/skybus_tunnel/web_socket_msg_t";
import { getWebsocketUri, IWebsocketOptions } from "@skydio/uri_util/src";

import { isArrayBuffer } from "../utils";

import { TransportLayer } from "../base";
import { ISkybusOptions, ISkybusStats } from "../types";
import WebsocketConnector from "./websocket_connector";
import { logger } from "../../logger";

/**
 * Skybus JS
 *
 * Javascript API for communicating with the Skybus pub/sub system.
 */
export default class WebsocketTransportLayer extends TransportLayer {
  public stats: ISkybusStats;

  // tslint:disable-next-line: variable-name
  private _isConnected: boolean = false;
  private uri?: string;
  private onError?: (error: Error) => void;
  private socket?: Websocket;
  private connectionCount: number;

  private connectPromise?: Promise<{}>;
  private connectPromiseResolver?: (value: {} | PromiseLike<{}>) => void;
  private connectPromiseRejector?: (reason: any) => void;

  private connector?: WebsocketConnector;

  /**
   * Construct with options object
   *
   * @param {ISkybusOptions} options {
   *   onStateChange : callback invoked when connection status changes
   *           quiet : do not print anything
   *         verbose : print detailed information about everything
   * }
   */
  constructor(options?: ISkybusOptions) {
    super(options);
    this.connectionCount = 0;

    this.stats = {
      downloadBytes: 0,
      uploadBytes: 0,
      errors: 0,
    };

    // Create the underlying websocket
    this.resetConnectPromise();
  }

  get isConnected() {
    return this._isConnected;
  }

  public createConnector(): WebsocketConnector {
    const connector = new WebsocketConnector(this);
    this.connector = connector;
    return connector;
  }

  /**
   * Require client to call connect to initiate actual connection
   * to websocket server
   */
  public connect(options?: string | IWebsocketOptions) {
    if (this.socket) {
      return;
    }
    // Default values

    if (typeof options === "string") {
      this.uri = options;
    } else {
      this.uri = getWebsocketUri(options);
      if (options && options.onError) {
        this.onError = options.onError;
      }
    }

    this.socket = new Websocket(
      this.uri,
      this.handleWebsocketMessage,
      this.onSocketStateChange,
      false, // true for debug logs
      "arraybuffer",
      this.onError
    );
  }

  public async waitForConnect(): Promise<{}> {
    if (!this.connectPromise) {
      throw new Error("no connect promise made");
    }
    return this.connectPromise;
  }

  public async sendData(data: ArrayBufferLike) {
    if (!this.socket || !this.isConnected) {
      await this.waitForConnect();
    }
    if (!this.socket) {
      return;
    }

    this.socket.send(data);
  }

  public close() {
    if (!this.socket) {
      return;
    }
    this.socket.destroy();
    this.socket = undefined;
  }

  /**
   * Called when socket reconnects (duh).
   * Use it to request lost subscriptions, etc.
   */
  public onSocketReconnected() {
    //
  }

  private onSocketStateChange = (socket: Websocket) => {
    const connected = socket.connected;

    if (connected && !this.isConnected) {
      logger.log("Connected to ", this.uri);
      this.maybeResolveConnectPromise();
      if (this.connectionCount++ > 0) {
        logger.log(`Reconnected (count: ${this.connectionCount})`);
        this.onSocketReconnected();
      }
    } else if (!connected && this.isConnected) {
      logger.log("Websocket closed to ", this.uri);
      this.resetConnectPromise();
    }

    if (connected !== this.isConnected) {
      this._isConnected = connected;
      this.connectionStatusListeners.forEach(listener => listener(this.isConnected));
    }
  };

  private resetConnectPromise() {
    if (this.connectPromiseRejector) {
      this.connectPromiseRejector("");
    }
    this.connectPromise = new Promise((resolve, reject) => {
      // @ts-ignore
      this.connectPromiseResolver = resolve;
      this.connectPromiseRejector = reject;
    });
  }

  private maybeResolveConnectPromise() {
    if (this.connectPromiseResolver) {
      this.connectPromiseResolver({});
      this.connectPromiseResolver = undefined;
      this.connectPromiseRejector = undefined;
    }
  }

  private handleWebsocketMessage = (message: MessageEvent) => {
    if (!isArrayBuffer(message.data)) {
      throw new Error("Got unknown data from websocket, not a ArrayBuffer");
    }
    const wrappedMsg = new web_socket_msg_t().decode(new Uint8Array(message.data));
    const { channel } = wrappedMsg;

    // let user decide how to handle messages
    if (this.connector?.handleMessage(channel, wrappedMsg.encoded_msg)) {
      return;
    }

    logger.trace("got unrequested message", { channel });
  };
}
